import React, { useState, useEffect, useContext } from 'react';
import {
    Grid,
    Typography,
    Box,
    InputAdornment,
    Button,
    Chip,
    Avatar
} from '@mui/material';
import StyledAutocomplete from '../../components/UIComponents/StyledAutocomplete';
import CheckIcon from '@mui/icons-material/Check';
import DateRangeIcon from '@mui/icons-material/DateRange';
import WorkIcon from '@mui/icons-material/Work';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AlarmIcon from '@mui/icons-material/Alarm';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import StyledTextField from '../../components/UIComponents/StyledTextField';
import CustomCard from '../../components/UIComponents/CustomCard';
import BasicTable from '../../components/Table';
import moment from 'moment';
import * as XLSX from 'xlsx';
import CloseIcon from '@mui/icons-material/Close';
import { runReportData, Employeedetails } from '../../service/network';
import { AuthContext } from '../../context/auth.context';
import { useLoading } from "../../context/loading.context"
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

// Custom badge components for different statuses
const PresentBadge = ({ count }) => (
    <Chip
        avatar={<Avatar sx={{ bgcolor: 'success.light', color: "white" }}><CheckIcon fontSize="small" color='white' sx={{ color: "white" }} /></Avatar>}
        label={count}
        sx={{
            bgcolor: 'success.lighter',
            color: 'success.dark',
            fontWeight: 'bold',
            minWidth: 70
        }}
    />
);

const AbsentBadge = ({ count }) => (
    <Chip
        avatar={<Avatar sx={{ bgcolor: 'error.light' }}><CloseIcon fontSize="small" sx={{ color: "white" }} /></Avatar>}
        label={count}
        sx={{
            bgcolor: 'error.lighter',
            color: 'error.dark',
            fontWeight: 'bold',
            minWidth: 70
        }}
    />
);

const LateEntryBadge = ({ count }) => (
    <Chip
        avatar={<Avatar sx={{ bgcolor: 'warning.light' }}><AlarmIcon fontSize="small" sx={{ color: "white" }} /></Avatar>}
        label={count}
        sx={{
            bgcolor: 'warning.lighter',
            color: 'warning.dark',
            fontWeight: 'bold',
            minWidth: 70
        }}
    />
);

const EarlyExitBadge = ({ count }) => (
    <Chip
        avatar={<Avatar sx={{ bgcolor: 'info.light' }}><ExitToAppIcon fontSize="small" sx={{ color: "white" }} /></Avatar>}
        label={count}
        sx={{
            bgcolor: 'info.lighter',
            color: 'info.dark',
            fontWeight: 'bold',
            minWidth: 70
        }}
    />
);

export default function AttendanceSummary() {
    const [employeeName, setEmployeeName] = useState(null);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [startDate, setStartDate] = useState(moment().subtract(30, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [reportData, setReportData] = useState([]);
    const [tableColumns, setTableColumns] = useState([]);
    const [dateError, setDateError] = useState(null);
    const { selLocation } = useContext(AuthContext);
    const { showLoader, hideLoader } = useLoading();

    // Fetch employee options from Frappe API
    const fetchEmployeeOptions = async () => {
        try {
            const queryParams = new URLSearchParams({
                doctype: 'Employee',
                fields: JSON.stringify(['name', 'employee_name']),
                ...(selLocation[0] && { filters: JSON.stringify({ company: selLocation[0] }) })
            });

            const response = await Employeedetails(queryParams.toString());

            const options = response?.data?.message?.values?.map(emp => ({
                label: `${emp[1]} (${emp[0]})`,
                value: emp[0]
            })) || [];

            setEmployeeOptions(options);
        } catch (error) {
            console.error('Failed to fetch employee options:', error);
            setEmployeeOptions([]);
        }
    };

    const validateDateRange = (from, to) => {
        const fromDate = moment(from);
        const toDate = moment(to);

        if (fromDate.isAfter(toDate)) {
            setDateError('Start date cannot be after end date');
            return false;
        }

        setDateError(null);
        return true;
    };

    const handleDateChange = (type, value) => {
        if (type === 'start') {
            setStartDate(value);
            validateDateRange(value, endDate);
        } else {
            setEndDate(value);
            validateDateRange(startDate, value);
        }
    };

    // Function to consolidate employee data
    // Function to consolidate employee data
    // Function to consolidate employee data
    const consolidateEmployeeData = (rawData) => {
        const employeeMap = {};

        rawData.forEach(record => {
            const employeeId = record.employee;

            if (!employeeMap[employeeId]) {
                employeeMap[employeeId] = {
                    employee: employeeId,
                    employee_name: record.employee_name,
                    department: record.department || '',
                    present_days: 0,
                    absent_days: 0,
                    total_working_hours: 0,
                    late_entries: 0,
                    early_exits: 0
                };
            }

            // Use the exact field names from API response
            employeeMap[employeeId].present_days = record.present_days || 0;
            employeeMap[employeeId].absent_days = record.absent_days || 0;
            employeeMap[employeeId].total_working_hours = record.total_working_hours || 0;
            employeeMap[employeeId].late_entries = record.late_entry_count || 0;
            employeeMap[employeeId].early_exits = record.early_exit_count || 0;
        });

        return Object.values(employeeMap);
    };
    const fetchReportData = async () => {
        showLoader(true);

        if (dateError || !selLocation[0]) return;

        const filters = {
            from_date: startDate,
            to_date: endDate,
            company: selLocation[0],
            ...(employeeName && { employee: employeeName.value })
        };

        try {
            const res = await runReportData({
                report_name: "Attendance Summary Custom",
                filters: filters
            });

            const { result } = res.data.message;

            // Debug: Check what data you're receiving
            console.log("API Response:", result);

            // Consolidate the data
            const consolidatedData = consolidateEmployeeData(result);

            // Debug: Check consolidated data
            console.log("Consolidated Data:", consolidatedData);

            const columns = [
                { id: "employee", label: "Employee ID", accessor: "employee", flex: 1, minWidth: '150px', align: 'left' },
                { id: "employee_name", label: "Employee Name", accessor: "employee_name", flex: 1.5, minWidth: '180px', align: 'left' },
                {
                    id: "present_days",
                    label: "Present Days",
                    flex: 1,
                    minWidth: '120px',
                    align: 'center',
                    render: (row) => <PresentBadge count={row.present_days || 0} />
                },
                {
                    id: "absent_days",
                    label: "Absent Days",
                    flex: 1,
                    minWidth: '120px',
                    align: 'center',
                    render: (row) => <AbsentBadge count={row.absent_days || 0} />
                },
                // In the table columns configuration, update the render functions:
                {
                    id: "total_working_hours",
                    label: "Working Hours",
                    flex: 1,
                    minWidth: '150px',
                    align: 'center',
                    render: (row) => (
                        <Typography variant="body2" sx={{
                            fontWeight: 'bold',
                            color: (row.total_working_hours || 0) < 30 ? 'error.main' : 'success.main'
                        }}>
                            {(row.total_working_hours || 0).toFixed(2)} hrs
                        </Typography>
                    )
                },
                {
                    id: "late_entries",
                    label: "Late Entries",
                    flex: 1,
                    minWidth: '120px',
                    align: 'center',
                    render: (row) => <LateEntryBadge count={row.late_entries || 0} />
                },
                {
                    id: "early_exits",
                    label: "Early Exits",
                    flex: 1,
                    minWidth: '120px',
                    align: 'center',
                    render: (row) => <EarlyExitBadge count={row.early_exits || 0} />
                }
            ];

            setTableColumns(columns);
            setReportData(consolidatedData);
            hideLoader();

        } catch (error) {
            console.error("Error fetching report data:", error);
            hideLoader();
        }
    };

    useEffect(() => {
        fetchEmployeeOptions();
    }, [selLocation]);

    useEffect(() => {
        fetchReportData();
    }, [startDate, endDate, employeeName, selLocation]);

    return (
        <Box sx={{ width: '100%', p: { xs: 1, sm: 2 }, boxSizing: 'border-box' }}>
            <CustomCard
                bgColor="light"
                shadow="lg"
                borderRadius="xxl"
                sx={{
                    width: '100%',
                    minHeight: '80vh',
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    p: 3
                }}
            >
                <Box sx={{ marginTop: 3 }}>
                    <Typography variant="subTitle" gutterBottom color='primary.main'>
                        Employee Attendance Summary
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        Consolidated view of attendance patterns with visual indicators
                    </Typography>
                </Box>


                <Grid container spacing={3} mt={3}>
                    <Grid size={2}>
                        <StyledTextField
                            label="Start Date"
                            type="date"
                            value={startDate}
                            onChange={(e) => handleDateChange('start', e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <DateRangeIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                            inputProps={{ max: moment().format('YYYY-MM-DD') }}
                        />
                    </Grid>
                    <Grid size={2}>
                        <StyledTextField
                            label="End Date"
                            type="date"
                            value={endDate}
                            onChange={(e) => handleDateChange('end', e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <DateRangeIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                            inputProps={{ max: moment().format('YYYY-MM-DD') }}
                        />
                    </Grid>
                    {/* <Grid size={2}>
                        <StyledAutocomplete
                            label="Employee"
                            value={employeeName}
                            onChange={(event, newValue) => setEmployeeName(newValue)}
                            options={employeeOptions}
                            getOptionLabel={(option) => option.label}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <WorkIcon color="action" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid> */}
                    <Grid item xs={12} sm={2}>
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 1, mt: 1 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{
                                    borderRadius: '8px',
                                    textTransform: 'none',
                                    px: 3,
                                    mt: 2,
                                    height: '36px'
                                }}
                                onClick={() => exportToExcel(reportData, tableColumns)}
                                disabled={dateError}
                                startIcon={<FileDownloadOutlinedIcon />}
                            >
                                Export
                            </Button>
                        </Box>
                    </Grid>
                </Grid>

                <Box sx={{ flex: 1, mt: 3 }}>
                    <BasicTable
                        data={{ rows: tableColumns, columns: reportData }}
                        backgroundColor="light.main"
                        sx={{
                            '& .MuiTableCell-root': {
                                py: 1.5
                            }
                        }}
                    />
                </Box>
            </CustomCard>
        </Box>
    );
}

// Export to Excel
const exportToExcel = (data, columns) => {
    const exportData = data.map(row => {
        const formattedRow = {};
        columns.forEach(column => {
            if (column.render) {
                // For rendered columns, get the raw value
                formattedRow[column.label] = row[column.id] || '';
            } else {
                formattedRow[column.label] = row[column.accessor] || '';
            }
        });
        return formattedRow;
    });

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Attendance Summary");
    XLSX.writeFile(workbook, "attendance_summary.xlsx");
};